import { inject, Injectable } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { select, Store } from '@ngrx/store'
import { map, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { AppState } from '../index'
import { AuthService } from '../../../auth.service'
import { BackendService } from '../../../api/backend/backend.service'
import { convertPermissionsToEntitiesResponse } from '../../model/client.converter'
import { ProgramResponse } from '../../../api/model/backend.model'
import { UserRoles } from './../../../admin/model/admin.model'
import { selectAuthState } from './auth.selectors'
import * as fromAuthActions from './auth.actions'
import { AppInsightsService } from '../../../core/telemetry/app-insights.service'

@Injectable()
export class AuthEffects {
  private backendService = inject(BackendService)
  dialog = inject(MatDialog)
  private authService = inject(AuthService)
  private appInsights = inject(AppInsightsService)

  private actions$ = inject(Actions)
  private store = inject(Store<AppState>)

  authenticate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromAuthActions.identifySuccess),
      tap(() => {
        this.appInsights.startTrackEvent('Login:Phase:Permissions')
      }),
      switchMap(() => {
        return this.backendService.permissions()
      }),
      withLatestFrom(this.store.pipe(select(selectAuthState))),
      switchMap(([res, authState]) => {
        const actions = []
        if (res.error) {
          this.appInsights.stopTrackEvent('Login:Phase:Permissions', { error: 'true' })
          this.appInsights.trackEvent('Login:Failed', {
            step: 'permissions',
            error: String(res.error),
          })
          actions.push(fromAuthActions.authenticateFailure())
          actions.push(fromAuthActions.identifyPermissionsFailure({ error: res.error }))
        } else if (res.data !== undefined) {
          this.appInsights.stopTrackEvent('Login:Phase:Permissions', {
            programCount: res.data.programs?.length?.toString() ?? '0',
          })
          this.appInsights.trackEvent('Login:PermissionsLoaded')
          this.backendService
            .postEvent({
              eventType: 'User Login',
              eventDetails: {
                user: authState.username,
              },
            })
            .subscribe({ error: () => undefined })
          res.data.programs.forEach((program: ProgramResponse) => {
            if (res.data) {
              program.tower_preferences = res.data.structureTowerPreferences.find(
                (tp) => tp.structure_id === program.id,
              )
            }
          })
          actions.push(fromAuthActions.authenticateSuccess())
          actions.push(
            fromAuthActions.setAuthenticatingUser({
              authenticatingUser: res.data.authenticatingUser,
            }),
          )
          actions.push(
            fromAuthActions.setSalesforceUserRole({
              salesforceUserRole: res.data.salesforceUserRole,
            }),
          )
          actions.push(
            fromAuthActions.setSageLogicalRole({
              sageLogicalRole: res.data.sageLogicalRole as UserRoles,
            }),
          )
          actions.push(
            fromAuthActions.identifyPermissionsSuccess(
              convertPermissionsToEntitiesResponse(res.data),
            ),
          )
        }
        return actions
      }),
    )
  })

  verifyUserFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fromAuthActions.authenticateFailure),
        tap((_) => {
          this.appInsights.stopTrackEvent('Login:Flow', { error: 'true' })
          this.appInsights.loginSessionId = null
          sessionStorage.removeItem('loginSessionId')
          this.authService.signOut()
        }),
      )
    },
    { dispatch: false },
  )

  authenticateSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromAuthActions.authenticateSuccess),
      tap(() => {
        this.appInsights.startTrackEvent('Login:Phase:AnalyzeReToken')
      }),
      switchMap(() => {
        // tslint:disable-next-line: no-non-null-assertion
        return this.backendService.getAnalyzereToken()
      }),
      map((res) => {
        if (res.error) {
          this.appInsights.stopTrackEvent('Login:Phase:AnalyzeReToken', { error: 'true' })
          this.appInsights.trackEvent('Login:Failed', { step: 'analyzere-token' })
          this.appInsights.stopTrackEvent('Login:Flow', { error: 'true', step: 'analyzere-token' })
          this.appInsights.loginSessionId = null
          sessionStorage.removeItem('loginSessionId')
          return fromAuthActions.setAnalyzereToken({
            token: null,
          })
        } else {
          this.appInsights.stopTrackEvent('Login:Phase:AnalyzeReToken')
          this.appInsights.trackEvent('Login:AnalyzeReTokenAcquired')
          this.appInsights.stopTrackEvent('Login:Flow')
          this.appInsights.trackEvent('Login:Complete', {
            loginSessionId: this.appInsights.loginSessionId ?? '',
          })
          this.appInsights.loginSessionId = null
          sessionStorage.removeItem('loginSessionId')
          return fromAuthActions.setAnalyzereToken({
            // tslint:disable-next-line: no-non-null-assertion
            token: res.data!.authToken,
          })
        }
      }),
    )
  })

  tokenExpired(token: string) {
    const expiry = JSON.parse(atob(token.split('.')[1])).exp
    // tslint:disable-next-line: new-parens
    return Math.floor(new Date().getTime() / 1000) >= expiry
  }
}
